import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { Route } from 'react-router';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter, extractFromArray } from '../../../../utils';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Icon from '../../../common/base/Icon';
import Loading from '../../../common/base/Loading';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSection, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewBreadcrumbsTitle, ViewMetadataDate, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import NotFound from '../../DashboardScene/NotFound';
import { DestinationTypeIcon } from '../Destinations/DestinationTypeIcon';
import { OutpostDataCollapsibleSection } from '../Event/OutpostDataSection';
import { OutpostMetadataCollapsibleSection } from '../Event/OutpostMetadataSection';
import AttemptSidebar from './AttemptSidebar';
import PreviousAttemptsList from './PreviousAttemptsList';
import { getOutpostTenantLabel, useOutpostTenantsById } from '../utils/tenantDisplay';
import { OUTPOST_ATTEMPT_INCLUDES } from './utils';
import useRetryAttempt from './useRetryAttempt';
const AttemptView: React.FC = () => {
  const $ = _c(138);
  const {
    id: attempt_id
  } = useParams();
  const history = useHistory();
  const {
    pathname
  } = useLocation();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  let t0;
  if ($[0] !== query.selected_attempt_id) {
    t0 = extractFromArray(query.selected_attempt_id);
    $[0] = query.selected_attempt_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selected_attempt_id = t0 as string;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  let t1;
  if ($[2] !== attempt_id) {
    t1 = APIMethodKeys.outpost.attempts.get(attempt_id);
    $[2] = attempt_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== HookdeckAPI.outpost.attempts || $[5] !== attempt_id) {
    t2 = () => HookdeckAPI.outpost.attempts.get(attempt_id, {
      include: OUTPOST_ATTEMPT_INCLUDES
    });
    $[4] = HookdeckAPI.outpost.attempts;
    $[5] = attempt_id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      refreshInterval: 5000
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: attempt,
    error,
    mutate: mutate_attempt
  } = useSWR(t1, t2, t3);
  const event_id = attempt?.event_id;
  let t4;
  if ($[8] !== event_id) {
    t4 = event_id ? APIMethodKeys.outpost.attempts.list({
      event_id
    }) : null;
    $[8] = event_id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== HookdeckAPI.outpost.attempts || $[11] !== event_id) {
    t5 = () => HookdeckAPI.outpost.attempts.list({
      event_id,
      include: ["event"]
    });
    $[10] = HookdeckAPI.outpost.attempts;
    $[11] = event_id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data: previous_attempts,
    mutate: mutate_previous_attempts
  } = useSWR(t4, t5);
  let t6;
  if ($[13] !== attempt_id || $[14] !== history || $[15] !== mutate_attempt || $[16] !== mutate_previous_attempts) {
    t6 = async () => {
      await Promise.all([mutate_attempt(), mutate_previous_attempts()]);
      history.push(`/deliveries/${attempt_id}/previous-attempts`);
    };
    $[13] = attempt_id;
    $[14] = history;
    $[15] = mutate_attempt;
    $[16] = mutate_previous_attempts;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    retryAttempt,
    retrying_attempt_id
  } = useRetryAttempt(t6);
  let t7;
  if ($[18] !== attempt) {
    t7 = attempt?.destination ? APIMethodKeys.outpost.destinationTypes.get(attempt.destination.type) : null;
    $[18] = attempt;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== HookdeckAPI.outpost.destinationTypes || $[21] !== attempt?.destination?.type) {
    t8 = () => HookdeckAPI.outpost.destinationTypes.get(attempt?.destination?.type as string);
    $[20] = HookdeckAPI.outpost.destinationTypes;
    $[21] = attempt?.destination?.type;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const {
    data: destination_type
  } = useSWR(t7, t8);
  const t9 = attempt?.tenant_id;
  let t10;
  if ($[23] !== t9) {
    t10 = [t9];
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const {
    tenants_by_id
  } = useOutpostTenantsById(t10);
  const is_details_tab = pathname.split("/").length === 3;
  const current_tab = is_details_tab ? "details" : "previous_attempts";
  if (error?.response?.status === 404) {
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        to: "/deliveries",
        text: "All Deliveries"
      };
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== attempt_id) {
      t12 = <NotFound title="Attempt not found" description="This attempt may have been deleted or does not exist." id={attempt_id} link={t11} />;
      $[26] = attempt_id;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  if (!attempt) {
    let t11;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <StyledViewLayout><StyledViewContent><Div flex={{
            grow: true,
            align: "center",
            justify: "center"
          }}><Loading /></Div></StyledViewContent></StyledViewLayout>;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  let t11;
  if ($[29] !== attempt.event) {
    t11 = attempt.event?.metadata ? Object.fromEntries(Object.entries(attempt.event.metadata).map(_temp)) : {};
    $[29] = attempt.event;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const metadata_as_strings = t11;
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      label: "Event Deliveries",
      to: "/deliveries"
    };
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== attempt_id) {
    t13 = <ViewBreadcrumbsTitle breadcrumbs={[t12, {
      label: attempt_id,
      highlight: true
    }]} />;
    $[32] = attempt_id;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = retrying_attempt_id === attempt.id ? "loading" : "retry";
  const t15 = retrying_attempt_id === attempt.id;
  let t16;
  if ($[34] !== attempt || $[35] !== retryAttempt) {
    t16 = () => retryAttempt(attempt);
    $[34] = attempt;
    $[35] = retryAttempt;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t15 || $[39] !== t16) {
    t17 = <StyledViewNavActions><Button.Permission role="member" outline={true} icon={t14} disabled={t15} onClick={t16}>Retry</Button.Permission></StyledViewNavActions>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t13 || $[42] !== t17) {
    t18 = <StyledViewNavHeader>{t13}{t17}</StyledViewNavHeader>;
    $[41] = t13;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== attempt_id || $[45] !== history) {
    t19 = tab => {
      if (tab === "details") {
        history.push(`/deliveries/${attempt_id}`);
      } else {
        history.push(`/deliveries/${attempt_id}/previous-attempts`);
      }
    };
    $[44] = attempt_id;
    $[45] = history;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      key: "details",
      label: "Details"
    };
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = previous_attempts?.models.length;
  let t22;
  if ($[48] !== t21) {
    t22 = [t20, {
      key: "previous_attempts",
      label: "Previous Attempts",
      badge_label: t21,
      badge_theme: "muted"
    }];
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== current_tab || $[51] !== t19 || $[52] !== t22) {
    t23 = <StyledViewNavSecondary><Tabs active_tab={current_tab} border={false} onTabSelected={t19} tabs={t22} /></StyledViewNavSecondary>;
    $[50] = current_tab;
    $[51] = t19;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t18 || $[55] !== t23) {
    t24 = <StyledViewNav>{t18}{t23}</StyledViewNav>;
    $[54] = t18;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ViewSplitPaneSidebarHeader title="Event delivery details" />;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const t26 = attempt.status === "success";
  const t27 = attempt.status === "failed";
  let t28;
  if ($[58] !== attempt.status) {
    t28 = capitalizeFirstLetter(attempt.status);
    $[58] = attempt.status;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t26 || $[61] !== t27 || $[62] !== t28) {
    t29 = <ViewMetadataItem label="Status"><Badge subtle={true} success={t26} danger={t27}>{t28}</Badge></ViewMetadataItem>;
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const t30 = !attempt.code;
  const t31 = attempt.code ?? "\u2014";
  let t32;
  if ($[64] !== t30 || $[65] !== t31) {
    t32 = <ViewMetadataItem label="Response code"><Text mono={true} size="s" ellipsis={true} muted={t30}>{t31}</Text></ViewMetadataItem>;
    $[64] = t30;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== attempt.attempt_number) {
    t33 = <ViewMetadataItem label="Attempt number"><Badge neutral={true}>{attempt.attempt_number}</Badge></ViewMetadataItem>;
    $[67] = attempt.attempt_number;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      gap: 2,
      align: "center"
    };
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  const t35 = attempt.manual ? "Yes" : "No";
  let t36;
  if ($[70] !== attempt.manual) {
    t36 = attempt.manual && <Icon icon="manual" neutral={true} small={true} />;
    $[70] = attempt.manual;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== t35 || $[73] !== t36) {
    t37 = <ViewMetadataItem label="Manual retry"><Text mono={true} size="s" ellipsis={true} flex={t34}>{t35}{t36}</Text></ViewMetadataItem>;
    $[72] = t35;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t29 || $[76] !== t32 || $[77] !== t33 || $[78] !== t37) {
    t38 = <ViewSplitPaneSidebarSection>{t29}{t32}{t33}{t37}</ViewSplitPaneSidebarSection>;
    $[75] = t29;
    $[76] = t32;
    $[77] = t33;
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  const t39 = `/events/${attempt.event_id}`;
  let t40;
  if ($[80] !== attempt.event_id || $[81] !== t39) {
    t40 = <ViewMetadataItem label="Event"><Chip mono={true} outline={true} label={attempt.event_id} to={t39} /></ViewMetadataItem>;
    $[80] = attempt.event_id;
    $[81] = t39;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  const t41 = tenants_by_id[attempt.tenant_id];
  let t42;
  if ($[83] !== attempt.tenant_id || $[84] !== t41) {
    t42 = getOutpostTenantLabel(attempt.tenant_id, t41);
    $[83] = attempt.tenant_id;
    $[84] = t41;
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  const t43 = `/tenants/${attempt.tenant_id}`;
  let t44;
  if ($[86] !== t42 || $[87] !== t43) {
    t44 = <ViewMetadataItem label="Tenant"><Chip outline={true} label={t42} to={t43} /></ViewMetadataItem>;
    $[86] = t42;
    $[87] = t43;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== attempt.destination || $[90] !== attempt.destination_id || $[91] !== attempt.tenant_id || $[92] !== destination_type) {
    t45 = <ViewMetadataItem label="Destination">{attempt.destination ? <Chip outline={true} title={attempt.destination.target} label={<Div flex={{
        align: "center",
        gap: 1
      }}><DestinationTypeIcon type={attempt.destination.type} type_meta={destination_type} /><Text as="span" size="xs" semi={true} p={{
          x: 1
        }} ellipsis={true}>{attempt.destination.target}</Text></Div>} to={`/tenants/${attempt.tenant_id}/destinations/${attempt.destination_id}`} /> : <Text muted={true} size="s" ellipsis={true}>{attempt.destination_id} (deleted)</Text>}</ViewMetadataItem>;
    $[89] = attempt.destination;
    $[90] = attempt.destination_id;
    $[91] = attempt.tenant_id;
    $[92] = destination_type;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t40 || $[95] !== t44 || $[96] !== t45) {
    t46 = <ViewSplitPaneSidebarSection title="Event" bordered={true}>{t40}{t44}{t45}</ViewSplitPaneSidebarSection>;
    $[94] = t40;
    $[95] = t44;
    $[96] = t45;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  let t47;
  if ($[98] !== attempt.time) {
    t47 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}><ViewMetadataItem label="Time"><ViewMetadataDate date={attempt.time} /></ViewMetadataItem></ViewSplitPaneSidebarSection>;
    $[98] = attempt.time;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] !== t38 || $[101] !== t46 || $[102] !== t47) {
    t48 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t25}{t38}{t46}{t47}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[100] = t38;
    $[101] = t46;
    $[102] = t47;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <ViewSplitPaneContentHeader title="Request" />;
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  let t50;
  if ($[105] !== metadata_as_strings) {
    t50 = Object.keys(metadata_as_strings).length > 0 && <OutpostMetadataCollapsibleSection metadata={metadata_as_strings} embedded={false} />;
    $[105] = metadata_as_strings;
    $[106] = t50;
  } else {
    t50 = $[106];
  }
  let t51;
  if ($[107] !== attempt.event) {
    t51 = attempt.event?.data ? <OutpostDataCollapsibleSection data={attempt.event.data} embedded={false} /> : <Text muted={true}>No event data available for this attempt.</Text>;
    $[107] = attempt.event;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] !== t50 || $[110] !== t51) {
    t52 = <StyledViewSplitPaneSection>{t49}{t50}{t51}</StyledViewSplitPaneSection>;
    $[109] = t50;
    $[110] = t51;
    $[111] = t52;
  } else {
    t52 = $[111];
  }
  let t53;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <ViewSplitPaneContentHeader title="Response" />;
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  let t54;
  if ($[113] !== attempt.response_data) {
    t54 = <StyledViewSplitPaneSection>{t53}{attempt.response_data ? <OutpostDataCollapsibleSection data={attempt.response_data} embedded={false} /> : <Text muted={true}>No response data available for this attempt.</Text>}</StyledViewSplitPaneSection>;
    $[113] = attempt.response_data;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] !== t52 || $[116] !== t54) {
    t55 = <StyledViewSplitPaneContent>{t52}{t54}</StyledViewSplitPaneContent>;
    $[115] = t52;
    $[116] = t54;
    $[117] = t55;
  } else {
    t55 = $[117];
  }
  let t56;
  if ($[118] !== is_degraded_banner_active || $[119] !== t48 || $[120] !== t55) {
    t56 = <Route exact={true} path="/deliveries/:id"><StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t48}{t55}</StyledViewSplitPaneLayout></Route>;
    $[118] = is_degraded_banner_active;
    $[119] = t48;
    $[120] = t55;
    $[121] = t56;
  } else {
    t56 = $[121];
  }
  let t57;
  if ($[122] !== updateSearchQuery) {
    t57 = id => updateSearchQuery({
      selected_attempt_id: id
    });
    $[122] = updateSearchQuery;
    $[123] = t57;
  } else {
    t57 = $[123];
  }
  let t58;
  if ($[124] !== previous_attempts || $[125] !== selected_attempt_id || $[126] !== t57) {
    t58 = <Route exact={true} path="/deliveries/:id/previous-attempts"><PreviousAttemptsList attempts={previous_attempts} selected_attempt_id={selected_attempt_id} onAttemptSelected={t57} /></Route>;
    $[124] = previous_attempts;
    $[125] = selected_attempt_id;
    $[126] = t57;
    $[127] = t58;
  } else {
    t58 = $[127];
  }
  let t59;
  if ($[128] !== t24 || $[129] !== t56 || $[130] !== t58) {
    t59 = <StyledViewContent>{t24}{t56}{t58}</StyledViewContent>;
    $[128] = t24;
    $[129] = t56;
    $[130] = t58;
    $[131] = t59;
  } else {
    t59 = $[131];
  }
  let t60;
  if ($[132] !== selected_attempt_id || $[133] !== updateSearchQuery) {
    t60 = selected_attempt_id && <AttemptSidebar attempt_id={selected_attempt_id} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_attempt_id"]
    })} />;
    $[132] = selected_attempt_id;
    $[133] = updateSearchQuery;
    $[134] = t60;
  } else {
    t60 = $[134];
  }
  let t61;
  if ($[135] !== t59 || $[136] !== t60) {
    t61 = <StyledViewLayout>{t59}{t60}</StyledViewLayout>;
    $[135] = t59;
    $[136] = t60;
    $[137] = t61;
  } else {
    t61 = $[137];
  }
  return t61;
};
export default AttemptView;
function _temp(t0) {
  const [key, value] = t0;
  return [key, typeof value === "object" ? JSON.stringify(value) : String(value ?? "")];
}